import { useEffect, useState } from "react";
import { Lock, RotateCcw } from "lucide-react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { imgSrc, useContent } from "@/lib/content";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { GooglePreview, Guide, Note, SeoInput, SocialPreview } from "@/components/admin/SeoParts";

const URLH = "Indirizzo completo che inizia con https://";
const GLOBAL = [
    { k: "site_name", label: "Nome del sito", required: true },
    { k: "title_template", label: "Modello del titolo", required: true, hint: "%s viene sostituito dal titolo della pagina, es. «%s | EDI Assessment AI»" },
    { k: "default_description", label: "Descrizione predefinita", type: "textarea", required: true, guide: [120, 160] },
    { k: "base_url", label: "URL base canonico (dominio)", type: "url", required: true, hint: "Dominio del sito pubblicato, es. https://assessment.edi.it. Usato per canonical, sitemap e robots.txt" },
    { k: "google_verification", label: "Codice di verifica Google Search Console", hint: "Solo il valore dell'attributo content, senza tag HTML" },
    { k: "bing_verification", label: "Codice di verifica Bing Webmaster Tools", hint: "Solo il valore dell'attributo content, senza tag HTML" },
];
const ORG = [
    { k: "name", label: "Nome dell'organizzazione", required: true }, { k: "legal_name", label: "Ragione sociale (facoltativa)" },
    { k: "url", label: "Sito dell'organizzazione", type: "url", hint: "Vuoto = URL base canonico" }, { k: "email", label: "Email di contatto", type: "email" },
    { k: "phone", label: "Telefono" }, { k: "address", label: "Indirizzo" },
    ...[["linkedin", "LinkedIn"], ["facebook", "Facebook"], ["instagram", "Instagram"], ["youtube", "YouTube"], ["x", "X"]].map(([k, l]) => ({ k, label: `Profilo ${l}`, type: "url", hint: URLH })),
];
const PAGE = [
    { k: "title", label: "Titolo SEO", required: true }, { k: "description", label: "Meta description", type: "textarea", required: true, guide: [120, 160] },
    { k: "canonical", label: "URL canonico", type: "url" }, { k: "indexable", label: "Indicizzabile", type: "switch", hint: "Se disattivato la pagina riceve noindex e viene esclusa dalla sitemap" },
    { k: "og_title", label: "Titolo per i social (OG)", hint: "Vuoto = titolo SEO" }, { k: "og_description", label: "Descrizione per i social (OG)", type: "textarea", hint: "Vuoto = meta description" },
    { k: "og_image", label: "Immagine per i social (OG)", type: "select", options: [["social", "Immagine per i social (predefinita)"], ["hero", "Foto della home"], ["logo", "Logo"]] },
];
const FILES = [
    { k: "ai_training", label: "Consenti l'uso dei contenuti per l'addestramento dei modelli AI", type: "switch", hint: "Controlla GPTBot, ClaudeBot, Google-Extended, CCBot e Applebot-Extended. I motori di ricerca e di risposta restano sempre ammessi." },
    { k: "llms_enabled", label: "Pubblica llms.txt (facoltativo, non influisce sul posizionamento)", type: "switch" },
    { k: "llms_intro", label: "Testo introduttivo di llms.txt", type: "textarea" },
];
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const SeoForm = ({ id, title, section, path, fields, initial, readOnly, onSaved, children, extra, template }) => {
    const [draft, setDraft] = useState(initial);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(null);
    useEffect(() => { setDraft(initial); setErrors({}); }, [initial]);
    const q = path ? `?path=${encodeURIComponent(path)}` : "";
    const run = async (fn) => {
        setStatus(null);
        try { const { data } = await fn(); setErrors({}); setStatus({ kind: "success", text: data.message }); onSaved(data); }
        catch (err) { const fe = fieldErrors(err); setErrors(fe || {}); setStatus(fe ? null : { kind: "error", text: errorMessage(err) }); if (fe) document.getElementById(`${id}-${Object.keys(fe)[0]}`)?.focus(); }
    };
    const fullTitle = (template || "%s").replace("%s", draft.title || "");
    return (
        <section className="edi-card p-5 sm:p-6 space-y-4 min-w-0" aria-labelledby={`${id}-title`} data-testid={id}>
            <h3 id={`${id}-title`} className="text-xl font-bold">{title}</h3>
            {extra}
            <div className="grid gap-4 md:grid-cols-2">
                {fields.map((f) => (
                    <div key={f.k} className={f.type === "textarea" || f.type === "switch" || f.k === "title" ? "md:col-span-2" : ""}>
                        <SeoInput id={`${id}-${f.k}`} f={f} value={draft[f.k]} readOnly={readOnly} error={errors[f.k]}
                            onChange={(v) => { setDraft((d) => ({ ...d, [f.k]: v })); if (errors[f.k]) setErrors((e) => ({ ...e, [f.k]: null })); }}
                            guide={f.k === "title" && template ? <Guide id={`${id}-title-guide`} text={fullTitle} range={[50, 60]} label={`Titolo completo «${fullTitle}»: `} />
                                : f.guide ? <Guide id={`${id}-${f.k}-guide`} text={draft[f.k] || ""} range={f.guide} /> : null} />
                    </div>
                ))}
            </div>
            {children?.(draft, fullTitle)}
            <div className="flex flex-wrap items-center gap-3">
                {!readOnly && <button type="button" className="btn-primary py-2" disabled={same(draft, initial)} onClick={() => run(() => api.put(`/admin/seo/${section}${q}`, { values: draft }))} data-testid={`${id}-save`}>Pubblica</button>}
                {!readOnly && <button type="button" className="btn-secondary py-2" onClick={() => run(() => api.delete(`/admin/seo/${section}${q}`))} data-testid={`${id}-restore`}><RotateCcw aria-hidden="true" size={16} /> Ripristina originale</button>}
                {status && <p role="status" className={`text-sm font-semibold ${status.kind === "error" ? "text-edi-error" : "text-edi-success"}`} data-testid={`${id}-status`}>{status.text}</p>}
            </div>
        </section>
    );
};

const Generated = ({ name, body }) => (
    <div className="space-y-2 min-w-0" data-testid={`seo-file-${name.replace(".", "-")}`}>
        <h4 className="font-bold">/{name}</h4>
        <Note testId={`seo-note-${name.replace(".", "-")}`} />
        <pre className="max-h-64 overflow-auto rounded-lg border border-edi-border bg-edi-bg p-3 text-xs whitespace-pre-wrap break-all" tabIndex={0} aria-label={`Contenuto generato di ${name}`}>{body ?? "Non pubblicato (disattivato)"}</pre>
    </div>
);

export const SeoPanel = ({ readOnly }) => {
    const { images, reload } = useContent();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [pagePath, setPagePath] = useState("/");
    useEffect(() => { api.get("/admin/seo").then(({ data: d }) => setData(d)).catch((e) => setErr(errorMessage(e))); }, []);
    if (err) return <Notice kind="error" role="alert">{err}</Notice>;
    if (!data) return <Loading />;
    const saved = (d) => { setData(d); reload(); };
    const page = data.pages.find((p) => p.path === pagePath);
    const base = data.global.base_url;
    const pageFields = PAGE.map((f) => (f.k === "title" ? { ...f, placeholder: page.default_title } : f.k === "description" ? { ...f, placeholder: page.default_description } : f.k === "canonical" ? { ...f, placeholder: base + page.path, hint: `Vuoto = ${base}${page.path}` } : f));
    const initialPage = { ...page.values, title: page.values.title || page.default_title, description: page.values.description || page.default_description };
    return (
        <div className="space-y-6 min-w-0" data-testid="seo-panel">
            <SeoForm id="seo-global" title="Impostazioni globali" section="global" fields={GLOBAL} initial={data.global} readOnly={readOnly} onSaved={saved}
                extra={<p className="text-sm"><span className="font-semibold">Lingua:</span> <span data-testid="seo-language">it-IT</span> (fissa)</p>} />
            <SeoForm id="seo-org" title="Organizzazione (dati strutturati)" section="org" fields={ORG} initial={data.org} readOnly={readOnly} onSaved={saved}
                extra={(
                    <div className="flex items-center gap-3">
                        <img src={imgSrc(images.logo.url)} alt={`Logo attuale: ${images.logo.alt}`} className="h-10 w-auto rounded border border-edi-border bg-white p-1" data-testid="seo-org-logo" />
                        <p className="text-sm text-edi-muted">Il logo è quello della sezione Immagini.</p>
                    </div>
                )} />
            <section className="space-y-4 min-w-0" aria-labelledby="seo-pages-title">
                <h3 id="seo-pages-title" className="text-xl font-bold">Pagine pubbliche</h3>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Scegli la pagina da modificare">
                    {data.pages.map((p) => (
                        <button key={p.path} type="button" aria-pressed={p.path === pagePath} onClick={() => setPagePath(p.path)} data-testid={`seo-page-btn-${p.path === "/" ? "home" : p.path.slice(1)}`}
                            className={`rounded-lg px-4 py-2 font-semibold ${p.path === pagePath ? "edi-active" : "bg-edi-tertiary hover:bg-edi-tertiary-hover"}`}>
                            {p.label}{!p.values.indexable && <span className="text-xs font-normal"> · noindex</span>}
                        </button>
                    ))}
                </div>
                <SeoForm key={page.path} id="seo-page" title={`Pagina «${page.label}» (${page.path})`} section="page" path={page.path} fields={pageFields}
                    initial={initialPage} readOnly={readOnly} onSaved={saved} template={data.global.title_template}>
                    {(d, fullTitle) => (
                        <div className="grid gap-4 lg:grid-cols-2">
                            <GooglePreview title={fullTitle} url={d.canonical || base + page.path} description={d.description} />
                            <SocialPreview image={imgSrc(images[d.og_image]?.url)} url={d.canonical || base + page.path} title={d.og_title || fullTitle} description={d.og_description || d.description} />
                        </div>
                    )}
                </SeoForm>
            </section>
            <section className="edi-card p-5 sm:p-6 space-y-3" aria-labelledby="seo-private-title" data-testid="seo-private">
                <h3 id="seo-private-title" className="text-xl font-bold">Pagine private</h3>
                <p className="text-sm text-edi-muted">Non modificabili: ricevono sempre noindex, nofollow, sono escluse dalla sitemap e vietate in robots.txt.</p>
                <ul className="divide-y divide-edi-border">
                    {data.private.map((p) => (
                        <li key={p.path} className="flex flex-wrap items-center justify-between gap-2 py-2">
                            <span><span className="font-semibold">{p.label}</span> <span className="text-sm text-edi-muted break-all">{p.path}</span></span>
                            <span className="inline-flex items-center gap-1 text-sm font-semibold"><Lock aria-hidden="true" size={14} /> Sempre esclusa (noindex)</span>
                        </li>
                    ))}
                </ul>
            </section>
            <SeoForm id="seo-files" title="robots.txt, sitemap.xml e llms.txt" section="files" fields={FILES} initial={data.files} readOnly={readOnly} onSaved={saved}
                extra={<Note testId="seo-note-ai-training" />}>
                {() => (
                    <div className="grid gap-4 xl:grid-cols-3">
                        {["robots.txt", "sitemap.xml", "llms.txt"].map((n) => <Generated key={n} name={n} body={data.generated[n]} />)}
                    </div>
                )}
            </SeoForm>
        </div>
    );
};
